/**
 * DOJA COOKIES — Delivery & Pickup picker
 * Requires: delivery-config.js loaded before this file.
 *
 * The picker:
 *  1. Starts with NO zone selected — the customer must tap one before
 *     any date appears (restores their zone only if this cart already
 *     saved a delivery_choice). No silent default: pre-selecting East
 *     Cairo let Giza/6-Oct customers book weekday dates for a
 *     Sundays-only area (order 001-1907).
 *  2. Renders 3 quick-pick date chips (next available dates) + a
 *     collapsible full calendar
 *  3. Writes cart attributes (delivery_choice, delivery_date,
 *     delivery_date_pretty, pickup_address) via /cart/update.js —
 *     this is the ONLY thing the picker writes to the cart. The
 *     delivery bot + admin order read these attributes.
 *  4. Gates the Place Order button: incomplete state → shake;
 *     pickup → redirect via DOJA_PICKUP discount; delivery → /checkout
 *  5. Persists chosen zone in localStorage
 *
 * NOTE: The picker deliberately does NOT touch line items. It used to
 * stamp Doja Choice/Date/Pickup properties onto each line via
 * /cart/change.js (and inject them into /cart/add), but those writes
 * mutated line keys and raced with the cart-drawer refresh, which
 * silently dropped quantities at checkout (2 → 1). The same info lives
 * in the cart attributes above, so line-item stamping was removed.
 */
(function () {
  'use strict';
  const CFG = window.DOJA_DELIVERY_CONFIG;
  if (!CFG) { console.error('[Doja Delivery] Config not loaded'); return; }

  const ATTR = {
    choice:     'delivery_choice',
    date:       'delivery_date',
    datePretty: 'delivery_date_pretty',
    pickupAddr: 'pickup_address',
  };
  const STORAGE_KEY = 'doja_last_choice';
  const SAVED_EVENT = 'doja:saved';
  const INIT_FLAG = 'data-doja-inited';

  const pad      = n => String(n).padStart(2, '0');
  const toISO    = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const fromISO  = s => { const [y,m,d] = s.split('-').map(Number); return new Date(y, m-1, d); };
  const stripTime= d => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const sameDay  = (a,b) => a.getFullYear()===b.getFullYear() && a.getMonth()===b.getMonth() && a.getDate()===b.getDate();
  const addDays  = (d,n) => { const x = new Date(d); x.setDate(x.getDate()+n); return x; };
  const isBlackout = iso => (CFG.blackout_dates || []).includes(iso);
  const prettyDate = iso => fromISO(iso).toLocaleDateString('en-US', { weekday:'long', month:'long', day:'numeric', year:'numeric' });

  const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const DAYS_SHORT = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
  const DAYS_FULL  = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

  function getChoice(id) { return (CFG.choices || []).find(c => c.id === id) || null; }

  function isDateAvailable(date, choiceId) {
    const c = getChoice(choiceId); if (!c) return false;
    const iso = toISO(date); if (isBlackout(iso)) return false;
    const today = stripTime(new Date());
    const dow = date.getDay();

    if (c.kind === 'pickup') {
      if (date < today) return false;
      const max = addDays(today, CFG.pickup.max_days_ahead || 60);
      if (date > max) return false;
      if (!(c.available_days || []).includes(dow)) return false;
      if (sameDay(date, today)) {
        const now = new Date();
        const cutoff = (CFG.pickup.hours_end || 20) - (CFG.pickup.min_lead_time_hours || 0);
        if (now.getHours() >= cutoff) return false;
      }
      return true;
    }
    const min = addDays(today, CFG.min_delivery_lead_days || 1);
    if (date < min) return false;
    const max = addDays(today, CFG.max_delivery_days_ahead || 60);
    if (date > max) return false;
    if (dow === 5 && CFG.deliver_on_fridays === false) return false;
    return (c.available_days || []).includes(dow);
  }

  function nextAvailableDates(choiceId, count) {
    const out = [];
    const today = stripTime(new Date());
    for (let i = 0; i < 90 && out.length < count; i++) {
      const probe = addDays(today, i);
      if (isDateAvailable(probe, choiceId)) out.push(probe);
    }
    return out;
  }

  function relativeLabel(date) {
    const today = stripTime(new Date());
    const diff = Math.round((date - today) / 86400000);
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Tomorrow';
    if (diff === 2) return 'Day after tomorrow';
    if (diff < 7) return `This ${DAYS_FULL[date.getDay()]}`;
    return `${DAYS_SHORT[date.getDay()]}, ${date.getDate()} ${MONTHS[date.getMonth()].slice(0,3)}`;
  }

  // Short form for the narrow date chips: keep the warm words for the near dates,
  // fall back to the 3-letter weekday for anything that wouldn't fit a column.
  function compactLabel(date) {
    const today = stripTime(new Date());
    const diff = Math.round((date - today) / 86400000);
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Tomorrow';
    return DAYS_SHORT[date.getDay()];
  }

  const latest = { choice: null, date: null };

  function createController(section) {
    if (section.hasAttribute(INIT_FLAG)) return;
    section.setAttribute(INIT_FLAG, 'true');
    const $  = (s, r) => (r || section).querySelector(s);
    const state = { choice: null, date: null, cal: stripTime(new Date()) };

    function zoneLabel(c) {
      return c ? c.label.replace(' Delivery', '').replace('Doja ', '') : '';
    }

    // A short schedule word for the collapsed summary — the full sub ("Giza, 6
    // October, Sheikh Zayed, Maadi · Sundays only · book…") is far too long for one line.
    function zoneShort(c) {
      if (!c) return '';
      if (c.kind === 'pickup') return 'pickup';
      const days = c.available_days || [];
      if (days.length === 1 && days[0] === 0) return 'Sundays';
      if (days.length >= 7) return 'daily';
      return '';
    }

    // The row splits the merchant-editable `sub` on "·" rather than hard-coding
    // strings, so editing the sub-text in theme settings can never make a row lie:
    //   "Tagamo3, Heliopolis, Nasr City · daily"          → areas / "daily"
    //   "Giza, 6 October, … , Maadi · Sundays only · …"   → areas / "Sundays only"
    //   "Obour City · 1pm – 8pm daily"                    → areas / "1pm – 8pm daily"
    function subParts(c) {
      return String((c && c.sub) || '').split('·').map(s => s.trim()).filter(Boolean);
    }
    // Left: the areas this zone covers. A West customer MUST be able to see that
    // Maadi/Giza live here and not in East Cairo — that is the 001-1907 bug.
    function zoneAreas(c) { return subParts(c)[0] || ''; }
    // Right: when it runs. Falls back to the derived word if the sub has no 2nd part.
    function zoneWhen(c) { return subParts(c)[1] || zoneShort(c); }

    // The rows are ALWAYS visible — no collapse/summary-bar/"Change" mechanic (the
    // user rejected it). The selected row just stays highlighted; switching area is
    // tapping another row.
    function renderZonePills() {
      const wrap = $('[data-doja="zones"]'); if (!wrap) return;
      wrap.innerHTML = '';
      CFG.choices.forEach(c => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'zone-row' + (state.choice === c.id ? ' selected' : '');
        btn.setAttribute('data-choice', c.id);
        btn.setAttribute('aria-pressed', state.choice === c.id ? 'true' : 'false');
        btn.innerHTML = `
          <span class="zone-row__dot" aria-hidden="true"></span>
          <span class="zone-row__body">
            <span class="zone-row__name">${zoneLabel(c)}</span>
            <span class="zone-row__areas">${zoneAreas(c)}</span>
          </span>
          <span class="zone-row__when">${zoneWhen(c)}</span>`;
        btn.addEventListener('click', () => selectChoice(c.id));
        wrap.appendChild(btn);
      });
    }

    // Calendar open = the calendar owns the screen: the item list and the quick
    // chips hide (it replaces the chips as the date picker) and the rows compact.
    // Nothing scrolls internally and the Place Order button never moves.
    function setCalOpen(open) {
      section.classList.toggle('cal-open', open);
      const drawer = section.closest('.cart-drawer');
      if (drawer) drawer.classList.toggle('doja-cal-open', open);
      const more = $('[data-doja="more"]');
      if (more) {
        more.classList.toggle('expanded', open);
        const l = more.querySelector('[data-doja="more-label"]');
        if (l) l.textContent = open ? 'Hide calendar' : 'Pick another date';
      }
      const cal = $('[data-doja="full-cal"]');
      if (cal) cal.classList.toggle('show', open);
    }

    function renderPickupWarning() {
      const w = $('[data-doja="pickup-warning"]'); if (!w) return;
      const c = getChoice(state.choice);
      const isPickup = !!(c && c.kind === 'pickup');
      w.hidden = !isPickup;
      if (!isPickup) w.classList.remove('is-expanded');
      const mapsLink = w.querySelector('[data-doja="maps-link"]');
      if (mapsLink && c && c.pickupMapsUrl) mapsLink.href = c.pickupMapsUrl;
    }

    // THE GATE: no zone → no dates on screen at all, so a date can never be picked
    // against the wrong (unselected) zone. The "you still owe us an area" message
    // is not here — it's the amber, bobbing Place Order button (see updateCTA), which
    // sits in the sticky footer and therefore cannot scroll out of view.
    function renderZonePrompt() {
      const block = $('[data-doja="date-block"]');
      if (block) block.hidden = !state.choice;
    }

    function renderQuickChips() {
      const wrap = $('[data-doja="chips"]'); if (!wrap) return;
      const more = $('[data-doja="more"]');
      const label = $('[data-doja="date-label"]');
      // No zone chosen yet — the date block is hidden (see renderZonePrompt);
      // nothing to render until a zone forces a valid date set.
      if (!state.choice) {
        wrap.innerHTML = '';
        if (more) more.hidden = true;
        setCalOpen(false);
        return;
      }
      if (more) more.hidden = false;
      wrap.innerHTML = '';
      const dates = nextAvailableDates(state.choice, 3);
      dates.forEach((d) => {
        const iso = toISO(d);
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'qp-chip' + (state.date === iso ? ' selected' : '');
        chip.setAttribute('aria-pressed', state.date === iso ? 'true' : 'false');
        // Day word + date, nothing else. The ★/2/3 badges that used to sit on top were
        // noise — they ranked the dates without meaning anything. The long relative label
        // ("Day after tomorrow") won't fit this narrow column either; it still shows in
        // full on the confirmation line above.
        chip.innerHTML = `
          <span class="qp-chip-main">${compactLabel(d)}</span>
          <span class="qp-chip-sub">${d.getDate()} ${MONTHS[d.getMonth()].slice(0, 3)}</span>`;
        chip.addEventListener('click', () => { state.date = iso; renderQuickChips(); renderCalendar(); updateCTA(); save(); });
        wrap.appendChild(chip);
      });
      if (label) label.textContent = getChoice(state.choice).kind === 'pickup' ? 'Pickup date' : 'Delivery date';
    }

    function renderCalendar() {
      const root = $('[data-doja="calendar"]'); if (!root) return;
      if (!state.choice) { root.innerHTML = ''; return; }
      const monthStart = new Date(state.cal.getFullYear(), state.cal.getMonth(), 1);
      const monthName = monthStart.toLocaleString('en-US', { month: 'long', year: 'numeric' });
      let html = `
        <div class="doja-cal__nav">
          <button type="button" class="doja-cal__btn" data-doja-nav="prev" aria-label="Previous month">‹</button>
          <span class="doja-cal__month">${monthName}</span>
          <button type="button" class="doja-cal__btn" data-doja-nav="next" aria-label="Next month">›</button>
        </div>
        <div class="doja-cal__grid doja-cal__grid--head">
          ${DAYS_SHORT.map(w => `<div class="doja-cal__wd">${w}</div>`).join('')}
        </div>
        <div class="doja-cal__grid">`;
      const firstDow = monthStart.getDay();
      const daysInMonth = new Date(monthStart.getFullYear(), monthStart.getMonth()+1, 0).getDate();
      const todayISO = toISO(new Date());
      for (let i = 0; i < firstDow; i++) html += '<div></div>';
      for (let d = 1; d <= daysInMonth; d++) {
        const date = new Date(monthStart.getFullYear(), monthStart.getMonth(), d);
        const iso = toISO(date);
        const ok = isDateAvailable(date, state.choice);
        const sel = state.date === iso;
        const isToday = iso === todayISO;
        const isBlk = isBlackout(iso);
        const cls = ['doja-cal__day'];
        if (!ok) cls.push('is-disabled');
        if (sel) cls.push('is-selected');
        if (isToday) cls.push('is-today');
        if (isBlk) cls.push('is-blackout');
        const attrs = isBlk
          ? `data-doja-blackout="${iso}" aria-disabled="true"`
          : (ok ? `data-doja-date="${iso}"` : `data-doja-date="${iso}" disabled aria-disabled="true"`);
        html += `<button type="button" class="${cls.join(' ')}" ${attrs}>${d}</button>`;
      }
      html += '</div>';
      html += '<div class="doja-cal__note" data-doja="blackout-note" hidden></div>';
      root.innerHTML = html;
    }

    function selectChoice(id) {
      state.choice = id;
      // Pre-select the SOONEST valid date for this zone instead of dropping the customer
      // onto a second locked button. This does NOT reopen the 001-1907 hole: that bug was
      // an unpicked ZONE inheriting East Cairo's daily dates, and the zone is still a
      // required, explicit tap. The date here comes from nextAvailableDates(id), so it is
      // zone-correct by construction — a West customer gets the next Sunday, never a
      // weekday. Any other chip (or the full calendar) still overrides it in one tap.
      const first = nextAvailableDates(id, 1)[0];
      state.date = first ? toISO(first) : null;
      try { localStorage.setItem(STORAGE_KEY, id); } catch (e) {}
      // A new zone always starts from the compact view — never the previous
      // zone's expanded month.
      state.cal = stripTime(new Date());
      setCalOpen(false);
      renderZonePrompt(); renderZonePills(); renderPickupWarning(); renderQuickChips(); renderCalendar(); updateCTA(); save();
    }

    function updateCTA() {
      const cta = document.querySelector('[data-doja="place-order"]');
      const text = cta?.querySelector('[data-doja="cta-text"]');
      const tip = $('[data-doja="tip"]');
      if (!cta || !text) return;
      latest.choice = state.choice; latest.date = state.date;
      const c = getChoice(state.choice);
      // .disabled turns the button gold + bobbing; .is-locked on the drawer foot makes
      // it fill the whole bottom area edge-to-edge (drawer only — the /cart page foot
      // has no .cart-drawer__foot, so closest() is null there and it stays a pill).
      const foot = cta.closest('.cart-drawer__foot');
      const locked = !state.choice || !state.date;
      if (foot) foot.classList.toggle('is-locked', locked);
      if (!state.choice) {
        cta.classList.add('disabled');
        // "Choose your area", not "…your delivery area": with the price sharing the row,
        // the longer label wrapped to two lines. The heading above it already says Delivery.
        text.textContent = 'Choose your area';
        // No tip: the whole date block is hidden until an area is picked, so nobody sees it.
        if (tip) tip.textContent = '';
      } else if (state.date) {
        cta.classList.remove('disabled');
        // Just "Place Order" — no "— Cash on Delivery" tail. The price next to it is
        // rendered bigger and should be the loudest thing on the button.
        text.textContent = 'Place Order';
        // NAME the date, don't just say "set". Since selectChoice now pre-picks the
        // soonest slot, a vague "✓ Date set" would let someone check out on a day they
        // never consciously chose. The selected chip is highlighted too.
        if (tip) {
          const d = fromISO(state.date);
          const rel = relativeLabel(d);
          // Far dates: relativeLabel is already "Sun, 26 Jul" — appending the date
          // again would print it twice. Near dates get "Tomorrow · 15 Jul".
          const dateBit = `${d.getDate()} ${MONTHS[d.getMonth()].slice(0, 3)}`;
          tip.textContent = rel.includes(dateBit) ? `✓ ${rel}` : `✓ ${rel} · ${dateBit}`;
          tip.style.color = 'var(--green)';
        }
      } else {
        cta.classList.add('disabled');
        text.textContent = c && c.kind === 'pickup' ? 'Pick a pickup date' : 'Pick a date to continue';
        if (tip) { tip.textContent = 'Tap a date'; tip.style.color = 'var(--cyan)'; }
      }
    }

    async function save() {
      const c = getChoice(state.choice);
      const attributes = {
        [ATTR.choice]: state.choice || '',
        [ATTR.date]: state.date || '',
        [ATTR.datePretty]: state.date ? prettyDate(state.date) : '',
        [ATTR.pickupAddr]: (c && c.kind === 'pickup' && c.pickupAddress) ? c.pickupAddress : '',
        '_doja_rate_bust': Date.now().toString(),
      };
      try {
        await fetch('/cart/update.js', {
          method:'POST',
          headers:{'Content-Type':'application/json','Accept':'application/json'},
          body: JSON.stringify({ attributes }),
        });
        document.dispatchEvent(new CustomEvent(SAVED_EVENT, { detail: { source: section } }));
      } catch (e) { console.error('[Doja Delivery] save failed', e); }
    }

    async function hydrateFromCart() {
      try {
        const r = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
        const cart = await r.json();
        const a = cart.attributes || {};
        // Restore ONLY a zone this cart already saved. No localStorage /
        // default fallback — a fresh cart starts with no zone selected so
        // the customer must pick their area before any date (see 001-1907).
        state.choice = a[ATTR.choice] || null;
        state.date   = state.choice ? (a[ATTR.date] || null) : null;
        latest.choice = state.choice; latest.date = state.date;
      } catch (e) { state.choice = null; state.date = null; }
      renderZonePrompt(); renderZonePills(); renderPickupWarning(); renderQuickChips(); renderCalendar(); updateCTA();
    }

    section.addEventListener('click', (e) => {
      const blk = e.target.closest('[data-doja-blackout]');
      if (blk) {
        const note = $('[data-doja="blackout-note"]');
        if (note) {
          const text = (CFG.blackout_note || '').trim();
          note.textContent = text || "We're closed on this date — please pick another day.";
          note.hidden = false;
          note.classList.remove('is-flash');
          void note.offsetWidth;
          note.classList.add('is-flash');
        }
        return;
      }
      const day = e.target.closest('[data-doja-date]');
      if (day && !day.disabled) {
        const iso = day.getAttribute('data-doja-date');
        const noteEl = $('[data-doja="blackout-note"]');
        if (noteEl) noteEl.hidden = true;
        state.date = iso;
        renderQuickChips(); renderCalendar(); updateCTA(); save();
        return;
      }
      const nav = e.target.closest('[data-doja-nav]');
      if (nav) {
        const dir = nav.getAttribute('data-doja-nav');
        const c = state.cal;
        state.cal = new Date(c.getFullYear(), c.getMonth() + (dir === 'next' ? 1 : -1), 1);
        renderCalendar(); return;
      }
      const more = e.target.closest('[data-doja="more"]');
      if (more) {
        setCalOpen(!section.classList.contains('cal-open'));
        return;
      }
      const warnTitle = e.target.closest('[data-doja="warn-title"]');
      if (warnTitle) {
        const w = $('[data-doja="pickup-warning"]');
        if (w) w.classList.toggle('is-expanded');
        return;
      }
    });

    document.addEventListener(SAVED_EVENT, (e) => { if (e.detail && e.detail.source === section) return; hydrateFromCart(); });
    document.addEventListener('doja:cart-updated', () => hydrateFromCart());
    hydrateFromCart();
  }

  /**
   * Bring the step that's blocking checkout into view and flash it.
   *
   * The shake alone told the customer "no" but never "why" or "where". On a full cart
   * the zone pills are scrolled off-screen behind the items, so tapping the big button
   * just made it wiggle at them with the answer hidden above the fold.
   */
  function nudgeToUnmetStep() {
    const section = [...document.querySelectorAll('.doja-delivery')]
      .find((s) => s.offsetParent !== null);
    if (!section) return;
    const target = !latest.choice
      ? section.querySelector('[data-doja="zones"]')
      : section.querySelector('[data-doja="chips"]');
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    target.classList.remove('doja-nudge');
    void target.offsetWidth; // restart the animation if it's already mid-flight
    target.classList.add('doja-nudge');
    setTimeout(() => target.classList.remove('doja-nudge'), 1400);
  }

  function gatePlaceOrder(e) {
    const btn = e.target.closest('[data-doja="place-order"]');
    if (!btn) return;
    e.preventDefault();
    if (!latest.choice || !latest.date) {
      btn.classList.add('shake');
      setTimeout(() => btn.classList.remove('shake'), 500);
      nudgeToUnmetStep();
      return;
    }
    const c = getChoice(latest.choice);
    if (c && c.kind === 'pickup') {
      window.location.href = '/discount/' + (CFG.pickup_discount_code || 'DOJA_PICKUP') + '?redirect=/checkout';
      return;
    }
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type':'application/json', 'Accept':'application/json' },
      body: JSON.stringify({ discount: '' }),
    }).finally(() => { window.location.href = '/checkout'; });
  }

  function ensureControllers() { document.querySelectorAll('.doja-delivery').forEach(createController); }
  function start() {
    ensureControllers();
    document.addEventListener('click', gatePlaceOrder, true);
    const mo = new MutationObserver(() => ensureControllers());
    mo.observe(document.body, { childList: true, subtree: true });
    document.addEventListener('shopify:section:load', ensureControllers);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
